/**
 * CR-FoldLine: Segmented control for fold line height selection
 * 
 * Provides Off / Normal (400px) / Large (600px) toggle.
 */

import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Ruler } from "lucide-react";
import type { FoldLineHeight } from "./FoldLineOverlay";

interface FoldLineControlProps {
  value: FoldLineHeight;
  onChange: (value: FoldLineHeight) => void;
  /** Whether to show the label */
  showLabel?: boolean;
  /** Compact mode for tight spaces */
  compact?: boolean;
}

const OPTIONS: { value: FoldLineHeight; label: string }[] = [
  { value: "off", label: "Off" },
  { value: "normal", label: "Normal (400px)" },
  { value: "large", label: "Large (600px)" },
];

export function FoldLineControl({
  value,
  onChange,
  showLabel = true,
  compact = false,
}: FoldLineControlProps) {
  return (
    <div className={compact ? "flex items-center gap-2" : "space-y-1"}>
      {showLabel && (
        <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
          <Ruler className="h-3.5 w-3.5" />
          <span>Card Cutoff (Above-the-fold)</span>
        </div>
      )}
      
      <ToggleGroup
        type="single"
        value={value}
        onValueChange={(v) => {
          if (v) onChange(v as FoldLineHeight);
        }}
        className="justify-start"
      >
        {OPTIONS.map((option) => (
          <ToggleGroupItem
            key={option.value}
            value={option.value}
            aria-label={option.label}
            className="text-xs px-2.5 py-1 h-7 data-[state=on]:bg-primary data-[state=on]:text-primary-foreground"
          >
            {compact ? option.label.replace(" (400px)", "").replace(" (600px)", "") : option.label}
          </ToggleGroupItem>
        ))}
      </ToggleGroup>
      
      {showLabel && !compact && (
        <p className="text-xs text-muted-foreground">
          Shows where the card is cropped in compact views. Preview still renders the full card.
        </p>
      )}
    </div>
  );
}

export default FoldLineControl;
